import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  IconButton,
  Tooltip,
  Popover,
  Paper,
  List,
  ListItem,
  ListItemText,
  Typography,
  Box,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import DownloadIcon from "@mui/icons-material/Download";
import axios from "axios";
import config from "../config/config";
import { useAuth } from "../pages/login/AuthContext";
import "./NotificationBell.css";

function getResimFullUrl(path) {
  if (!path) return "";
  const p = String(path).trim();
  if (p.startsWith("http")) return p;
  const m = p.match(/\/api\/uploads\/pop-up\/([^/]+)$/);
  if (m) return `${config.baseURL}/bildirim/pop-up-resim/${encodeURIComponent(m[1])}`;
  const filenameOnly = p.includes("/") ? p.replace(/^.*\//, "") : p;
  if (filenameOnly && /\.(jpe?g|png|gif|webp)$/i.test(filenameOnly))
    return `${config.baseURL}/bildirim/pop-up-resim/${encodeURIComponent(filenameOnly)}`;
  const base = config.baseURL.replace(/\/api\/?$/, "");
  return `${base}${p.startsWith("/") ? "" : "/"}${p}`;
}

function downloadResim(url, defaultFilename = "resim.jpg") {
  if (!url) return;
  const filename = (url.split("/").pop() || defaultFilename).split("?")[0] || defaultFilename;
  fetch(url, { credentials: "include" })
    .then((r) => r.blob())
    .then((blob) => {
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
    })
    .catch(() => window.open(url, "_blank"));
}

const TIP_LABELS = {
  sistem: "Sistem",
  siparis_olusturma: "Sipariş",
  siparis_yonlendirme: "Yönlendirme",
  siparis_onay: "Onay",
};

function formatDate(d) {
  if (!d) return "";
  const date = new Date(d);
  return date.toLocaleString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

export function NotificationBell() {
  const { token, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [markingAllRead, setMarkingAllRead] = useState(false);
  const [detayBildirim, setDetayBildirim] = useState(null);

  const open = Boolean(anchorEl);

  const fetchCount = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${config.baseURL}/bildirim/okunmamis-sayisi`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.success) setCount(res.data.count ?? 0);
    } catch (_) {
      setCount(0);
    }
  }, [token]);

  const fetchList = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${config.baseURL}/bildirim?limit=15`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.success) setList(res.data.data || []);
    } catch (_) {
      setList([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!isAuthenticated || !token) {
      setCount(0);
      return;
    }
    fetchCount();
    const interval = setInterval(fetchCount, 60000);
    return () => clearInterval(interval);
  }, [isAuthenticated, token, fetchCount]);

  useEffect(() => {
    if (open) {
      fetchList();
      fetchCount();
    }
  }, [open, fetchList, fetchCount]);

  const handleBellClick = (event) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMarkRead = async (id) => {
    try {
      await axios.put(`${config.baseURL}/bildirim/${id}/okundu`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setList((prev) => prev.map((b) => (b.Id === id ? { ...b, Okundu: true } : b)));
      fetchCount();
    } catch (_) {}
  };

  const handleMarkAllRead = async () => {
    if (!token || markingAllRead || count === 0) return;
    setMarkingAllRead(true);
    try {
      await axios.put(`${config.baseURL}/bildirim/tumunu-okundu`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setList((prev) => prev.map((b) => ({ ...b, Okundu: true })));
      setCount(0);
    } catch (_) {
      fetchCount();
    } finally {
      setMarkingAllRead(false);
    }
  };

  const handleTumunuGor = () => {
    handleClose();
    navigate("/sistem-mesajlari");
  };

  const handleBildirimClick = (b) => {
    setDetayBildirim(b);
    if (!b.Okundu) handleMarkRead(b.Id);
  };

  if (!isAuthenticated) return null;

  return (
    <div className="notification-bell-wrapper">
      <Tooltip title="Bildirimler">
        <IconButton
          className="notification-bell-btn"
          onClick={handleBellClick}
          aria-label="Bildirimler"
          size="medium"
          aria-expanded={open ? "true" : undefined}
          aria-haspopup="true"
        >
          <NotificationsIcon className="notification-bell-icon" />
          {count > 0 && (
            <span className="notification-bell-badge">
              {count > 99 ? "99+" : count}
            </span>
          )}
        </IconButton>
      </Tooltip>

      <Popover
        className="notification-bell-popover"
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            width: 360,
            maxHeight: 420,
            borderRadius: 2,
            boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
            mt: 1.5,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Box className="notification-bell-popover-header">
          <Typography variant="subtitle1" fontWeight={700} color="inherit">
            Bildirimler
            {count > 0 && (
              <span className="notification-bell-popover-count"> ({count})</span>
            )}
          </Typography>
        </Box>
        <Box className="notification-bell-popover-body">
          {loading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={28} sx={{ color: "var(--primary-color)" }} />
            </Box>
          ) : list.length === 0 ? (
            <Box py={3} px={2} textAlign="center">
              <NotificationsNoneIcon sx={{ fontSize: 48, color: "#ccc" }} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Henüz bildirim yok
              </Typography>
            </Box>
          ) : (
            <List disablePadding className="notification-bell-list">
              {list.map((b) => (
                <ListItem
                  key={b.Id}
                  className={`notification-bell-list-item ${b.Okundu ? "okundu" : ""}`}
                  onClick={() => handleBildirimClick(b)}
                  button
                  disableRipple
                  sx={{ cursor: "pointer" }}
                >
                  <ListItemText
                    primary={b.Baslik}
                    secondary={
                      <>
                        <Typography component="span" variant="body2" color="text.secondary" className="notification-bell-icerik">
                          {b.Icerik ? (b.Icerik.length > 60 ? b.Icerik.slice(0, 60) + "…" : b.Icerik) : ""}
                        </Typography>
                        <Typography component="span" variant="caption" display="block" color="text.secondary" sx={{ mt: 0.25 }}>
                          {TIP_LABELS[b.Tip] || b.Tip} · {b.CreatedAtTr || formatDate(b.CreatedAt)}
                        </Typography>
                      </>
                    }
                    primaryTypographyProps={{
                      fontWeight: b.Okundu ? 400 : 600,
                      fontSize: "0.9rem",
                    }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
        {list.length > 0 && (
          <Box className="notification-bell-popover-footer">
            <Button
              fullWidth
              size="small"
              onClick={handleMarkAllRead}
              disabled={count === 0 || markingAllRead}
              sx={{ color: "var(--primary-color)", fontWeight: 600, mb: 0.5 }}
            >
              {markingAllRead ? "İşaretleniyor..." : "Tümünü okundu olarak işaretle"}
            </Button>
            <Button
              fullWidth
              size="small"
              onClick={handleTumunuGor}
              sx={{ color: "var(--primary-color)", fontWeight: 600 }}
            >
              Tümünü gör
            </Button>
          </Box>
        )}
      </Popover>

      <Dialog
        open={Boolean(detayBildirim)}
        onClose={() => setDetayBildirim(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        {detayBildirim && (
          <>
            <DialogTitle sx={{ fontWeight: 600, color: "var(--primary-color)" }}>
              {detayBildirim.Baslik}
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {TIP_LABELS[detayBildirim.Tip] || detayBildirim.Tip} · {detayBildirim.CreatedAtTr || formatDate(detayBildirim.CreatedAt)}
              </Typography>
              {(() => {
                const resimler = Array.isArray(detayBildirim.PopUpResimler)
                  ? detayBildirim.PopUpResimler
                  : typeof detayBildirim.PopUpResimler === "string"
                    ? (() => { try { return JSON.parse(detayBildirim.PopUpResimler) || []; } catch { return []; } })()
                    : [];
                return resimler.length > 0 ? (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                    {resimler.map((path, idx) => {
                      const src = getResimFullUrl(path);
                      return (
                        <Box key={idx} sx={{ position: "relative", display: "inline-block" }}>
                          <Box
                            component="img"
                            src={src}
                            alt={`Ek ${idx + 1}`}
                            sx={{ maxWidth: "100%", maxHeight: 280, objectFit: "contain", borderRadius: 1, border: "1px solid #eee" }}
                            onError={(e) => { e.target.style.display = "none"; }}
                          />
                          <IconButton
                            onClick={() => downloadResim(src, `ek-${idx + 1}.jpg`)}
                            sx={{
                              position: "absolute",
                              top: 8,
                              right: 8,
                              bgcolor: "rgba(255,255,255,0.9)",
                              "&:hover": { bgcolor: "white" },
                            }}
                            size="small"
                            aria-label="İndir"
                          >
                            <DownloadIcon />
                          </IconButton>
                        </Box>
                      );
                    })}
                  </Box>
                ) : null;
              })()}
              <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                {detayBildirim.Icerik || "—"}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetayBildirim(null)} variant="contained">
                Kapat
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </div>
  );
}
